import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  bulkUpdatePolicies,
  createPolicyGroup,
  getPolicies,
  getPolicyGroups,
  getSitesOptions,
  updatePolicyGroup,
} from '@/services/asicw/policies.service'
import type { IPolicyFilters, IPolicyGroupUpdateForm } from '@/types/asicw/policies'
import type { IPolicyGroupFilters } from '@/types/asicw/policy-groups'
import type { ILiteSiteFilters } from '@/types/core/filters'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { Reactive, Ref } from 'vue'

export const usePolicies = () => {
  const queryClient = useQueryClient()

  const policyGroupsQuery = (filters: Reactive<IPolicyGroupFilters>) =>
    useQuery({
      queryFn: () => getPolicyGroups(filters),
      queryKey: ['policy-groups', filters],
    })

  const getPoliciesQuery = (filters: Reactive<IPolicyFilters>) =>
    useQuery({
      queryFn: () => getPolicies(filters),
      queryKey: ['policies', filters],
    })

  const createPolicyGroupMutation = useMutation({
    mutationFn: createPolicyGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-groups'] })
      queryClient.invalidateQueries({ queryKey: ['policies'] })
      NotifySuccess('Policy group was created')
    },
    onError: NotifyErrorWrapper('Failed to create policy group'),
  })

  const bulkUpdatePoliciesMutation = useMutation({
    mutationFn: bulkUpdatePolicies,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] })
      NotifySuccess('Policies updated successfully')
    },
    onError: NotifyErrorWrapper('Failed to update policies'),
  })

  const updatePolicyGroupMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: IPolicyGroupUpdateForm }) => updatePolicyGroup(id, form),
    onError: NotifyErrorWrapper('Failed to update policy group'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-groups'] })
      queryClient.invalidateQueries({ queryKey: ['policies'] })
      NotifySuccess(`Policy group updated successfully`)
    },
  })

  const getSiteOptionsQuery = (filters: Reactive<ILiteSiteFilters>, active?: Ref<boolean>) =>
    useQuery({
      queryFn: () => getSitesOptions(filters),
      queryKey: ['sites-lite', filters],
      enabled: active,
    })

  return {
    policyGroupsQuery,
    getPoliciesQuery,
    getSiteOptionsQuery,
    createPolicyGroupMutation,
    bulkUpdatePoliciesMutation,
    updatePolicyGroupMutation,
  }
}
